"use client";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import Select from "react-select";
import { format, parseISO, eachDayOfInterval, isWeekend } from "date-fns";
import AlertDialog from "./AlertDialog";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";

// ── Design tokens (dark ERP palette, orange = primary action only) ─────────
const C = {
  bg: "#0b0d11",
  panel: "#141821",
  panel2: "#1a1f2b",
  field: "#0e1117",
  line: "#2a3140",
  lineSoft: "#202634",
  text: "#eef1f6",
  sub: "#b4bccc",
  mute: "#808a9d",
  accent: "#f97316",
  accentSoft: "rgba(249,115,22,0.10)",
  ok: "#34d399",
  warn: "#fbbf24",
  bad: "#f87171",
  info: "#60a5fa",
};

const selectStyles = {
  control: (p, s) => ({
    ...p,
    backgroundColor: C.field,
    borderColor: s.isFocused ? C.accent : C.line,
    boxShadow: "none",
    "&:hover": { borderColor: C.accent },
    minHeight: "36px",
    fontSize: "13px",
    borderRadius: "6px",
  }),
  input: (p) => ({ ...p, color: C.text }),
  singleValue: (p) => ({ ...p, color: C.text, fontSize: "13px" }),
  placeholder: (p) => ({ ...p, color: C.mute, fontSize: "13px" }),
  dropdownIndicator: (p) => ({ ...p, color: C.mute }),
  indicatorSeparator: () => ({ display: "none" }),
  option: (p, s) => ({
    ...p,
    backgroundColor: s.isSelected
      ? C.accentSoft
      : s.isFocused
        ? C.panel2
        : C.panel,
    color: s.isSelected ? C.accent : C.text,
    fontSize: "13px",
    cursor: "pointer",
  }),
  menu: (p) => ({
    ...p,
    backgroundColor: C.panel,
    border: `1px solid ${C.line}`,
    borderRadius: "6px",
    zIndex: 60,
  }),
};

const SICK_RE = /sick|illness|medical/i;
const kindLabel = (k) => (k === "sick" ? "Sick leave" : "Vacation");

const RP_TAG = {
  PENDING: { color: C.warn, label: "Rippling: pending" },
  APPROVED: { color: C.ok, label: "Rippling: approved" },
  REJECTED: { color: C.bad, label: "Rippling: declined" },
  CANCELED: { color: C.mute, label: "Rippling: canceled" },
};

const cls = (...c) => c.filter(Boolean).join(" ");
const token = () => localStorage.getItem("token");
const headers = () => ({
  Authorization: `Bearer ${token()}`,
  "X-Tenant-ID": localStorage.getItem("tenantId") || "",
});

const money = (n) =>
  (Number(n) || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

const fmtHours = (minutes) =>
  minutes == null ? "—" : `${(minutes / 60).toFixed(2)}h`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${parseInt(m, 10)}/${parseInt(d, 10)}/${y}`;
};

const clock = (ts) =>
  ts
    ? new Date(ts).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

const inputCls =
  "w-full h-9 px-3 rounded-md bg-[#0e1117] border border-[#2a3140] text-[#eef1f6] text-[13px] placeholder:text-[#6b7588] outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 disabled:opacity-40";

// ── Small UI primitives ───────────────────────────────────────────────────
const Chevron = ({ open }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transform: open ? "rotate(90deg)" : "none",
      transition: "transform .15s",
      color: C.mute,
    }}
  >
    <path d="M7 4l6 6-6 6" />
  </svg>
);

const SearchIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <circle cx="9" cy="9" r="6" />
    <path d="M14 14l4 4" />
  </svg>
);

const STATUS_STYLE = {
  pending: { fg: C.warn, label: "Pending" },
  approved: { fg: C.ok, label: "Approved" },
  rejected: { fg: C.bad, label: "Rejected" },
  cancelled: { fg: C.mute, label: "Cancelled" },
};

const Pill = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide"
      style={{
        color: s.fg,
        border: `1px solid ${s.fg}55`,
        backgroundColor: `${s.fg}14`,
      }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: s.fg }}
      />
      {s.label}
    </span>
  );
};

const Tag = ({ color = C.sub, children }) => (
  <span
    className="inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-semibold uppercase tracking-wide"
    style={{
      color,
      border: `1px solid ${color}55`,
      backgroundColor: `${color}14`,
    }}
  >
    {children}
  </span>
);

const Switch = ({ checked, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={(e) => {
      e.stopPropagation();
      onChange(!checked);
    }}
    className="inline-flex items-center gap-2 text-[13px]"
    style={{ color: checked ? C.text : C.sub }}
  >
    <span
      className="relative inline-block w-9 h-5 rounded-full transition-colors"
      style={{ backgroundColor: checked ? C.accent : "#363e4f" }}
    >
      <span
        className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all"
        style={{ left: checked ? "18px" : "2px" }}
      />
    </span>
    {label}
  </button>
);

const Check = ({ checked, onChange, disabled }) => (
  <input
    type="checkbox"
    checked={checked}
    disabled={disabled}
    onChange={onChange}
    onClick={(e) => e.stopPropagation()}
    className="h-4 w-4 cursor-pointer"
    style={{ accentColor: C.accent }}
  />
);

// Collapsible section card
const Section = ({
  title,
  subtitle,
  summary,
  right,
  open,
  onToggle,
  children,
}) => (
  <div
    className="rounded-lg mb-4 overflow-hidden"
    style={{ backgroundColor: C.panel, border: `1px solid ${C.line}` }}
  >
    <div
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onToggle()}
      className="flex items-center justify-between gap-3 px-5 py-3.5 cursor-pointer select-none"
      style={{
        backgroundColor: C.panel2,
        borderBottom: open ? `1px solid ${C.line}` : "none",
      }}
    >
      <div className="flex items-center gap-3 min-w-0">
        <Chevron open={open} />
        <div className="min-w-0">
          <h3 className="text-[14px] font-semibold" style={{ color: C.text }}>
            {title}
          </h3>
          {subtitle && (
            <p className="text-[12px] mt-0.5" style={{ color: C.mute }}>
              {subtitle}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {summary}
        {right}
      </div>
    </div>
    {open && <div className="p-5">{children}</div>}
  </div>
);

const Th = ({ children, right, className }) => (
  <th
    className={cls(
      "px-3 py-2.5 text-[11px] uppercase tracking-wider font-semibold whitespace-nowrap",
      right ? "text-right" : "text-left",
      className,
    )}
    style={{ color: C.mute, backgroundColor: C.panel2 }}
  >
    {children}
  </th>
);

const Btn = ({ variant = "secondary", disabled, onClick, children, full }) => {
  const v = {
    primary: { bg: C.accent, fg: "#fff", bd: C.accent },
    success: { bg: "#16a34a", fg: "#fff", bd: "#16a34a" },
    danger: { bg: "#b91c1c", fg: "#fff", bd: "#b91c1c" },
    secondary: { bg: C.panel2, fg: C.text, bd: C.line },
  }[variant];
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cls(
        "h-9 px-4 text-[13px] font-semibold rounded-md hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition",
        full && "w-full",
      )}
      style={{
        backgroundColor: v.bg,
        color: v.fg,
        border: `1px solid ${v.bd}`,
      }}
    >
      {children}
    </button>
  );
};

const Field = ({ label, children }) => (
  <div>
    <p
      className="text-[10.5px] uppercase tracking-wider font-semibold"
      style={{ color: C.mute }}
    >
      {label}
    </p>
    <div className="text-[13px] mt-1" style={{ color: C.text }}>
      {children}
    </div>
  </div>
);

const Label = ({ children }) => (
  <label
    className="block text-[12px] font-semibold mb-1.5"
    style={{ color: C.sub }}
  >
    {children}
  </label>
);

// ── Submission card (shared by "My submissions" and "Approvals") ──────────
function SubmissionCard({
  sub,
  isAdminView,
  meId,
  busy,
  onApprove,
  onReject,
  onCancel,
  onRetry,
}) {
  const [open, setOpen] = useState(sub.status === "pending");
  const [live, setLive] = useState(null);
  const [liveErr, setLiveErr] = useState("");
  const v = sub.vacation;
  const declinedInRippling =
    sub.status === "pending" &&
    ["REJECTED", "CANCELED"].includes(v?.ripplingStatus);
  const isMine =
    String(sub.submittedBy?._id || sub.submittedBy) === String(meId);

  const checkLive = async () => {
    setLiveErr("");
    try {
      const res = await axios.get(
        `${API}/payrollSubmissions/${sub._id}/vacation-balance`,
        { headers: headers() },
      );
      setLive(res.data);
    } catch (err) {
      setLiveErr(err.response?.data?.message || err.message);
    }
  };

  const chips = [
    sub.timesheets.length > 0 &&
      `${sub.totals.hours.toFixed(2)}h · ${sub.timesheets.length} timesheet${sub.timesheets.length > 1 ? "s" : ""}`,
    sub.salary?.included && `Salary ${sub.salary.hours}h`,
    v && `${kindLabel(v.kind)} ${fmtHours(v.totalMinutes)}`,
    sub.expenses.length > 0 && `${money(sub.totals.expenses)} reimbursement`,
  ].filter(Boolean);

  return (
    <div
      className="rounded-lg mb-3 overflow-hidden"
      style={{ backgroundColor: C.panel, border: `1px solid ${C.line}` }}
    >
      <div
        className="flex items-center justify-between gap-3 px-5 py-3.5 cursor-pointer"
        style={{ backgroundColor: C.panel2 }}
        onClick={() => setOpen((o) => !o)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <Chevron open={open} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="text-[14px] font-semibold"
                style={{ color: C.text }}
              >
                {isAdminView
                  ? sub.submittedBy?.displayName || "Unknown"
                  : `Week of ${fmtDate(sub.weekStartISO)}`}
              </span>
              <Pill status={sub.status} />
              {v?.ripplingStatus === "SYNC_FAILED" && (
                <Tag color={C.bad}>Rippling sync failed</Tag>
              )}
              {RP_TAG[v?.ripplingStatus] && (
                <Tag color={RP_TAG[v.ripplingStatus].color}>
                  {RP_TAG[v.ripplingStatus].label}
                </Tag>
              )}
            </div>
            <p className="text-[12px] mt-1" style={{ color: C.mute }}>
              {isAdminView && `Week of ${fmtDate(sub.weekStartISO)} · `}
              {chips.join(" · ")}
              {chips.length > 0 && " · "}
              submitted {format(new Date(sub.submittedAt), "MMM d, h:mm a")}
            </p>
          </div>
        </div>
      </div>

      {open && (
        <div className="p-5">
          {declinedInRippling && (
            <div
              className="rounded-md px-4 py-3 mb-4 text-[13px]"
              style={{
                color: C.bad,
                backgroundColor: `${C.bad}14`,
                border: `1px solid ${C.bad}55`,
              }}
            >
              This time-off request was{" "}
              {v.ripplingStatus === "REJECTED" ? "declined" : "canceled"} in
              Rippling.{" "}
              {isAdminView
                ? "Reject or cancel this submission and ask for a new one."
                : "Cancel this submission and submit again."}
            </div>
          )}
          {sub.timesheets.length > 0 && (
            <div className="mb-5">
              <p
                className="text-[12px] font-semibold mb-2"
                style={{ color: C.sub }}
              >
                Timesheets
              </p>
              <div
                className="overflow-x-auto rounded-md"
                style={{ border: `1px solid ${C.line}` }}
              >
                <table className="min-w-full">
                  <thead>
                    <tr>
                      <Th>Date</Th>
                      <Th>Project</Th>
                      <Th>WO</Th>
                      <Th>In</Th>
                      <Th>Out</Th>
                      <Th right>Hours</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {sub.timesheets.map((t, i) => (
                      <tr
                        key={t._id}
                        style={{
                          borderTop: i ? `1px solid ${C.lineSoft}` : "none",
                        }}
                      >
                        <td
                          className="px-3 py-2 text-[13px]"
                          style={{ color: C.text }}
                        >
                          {new Date(t.start).toLocaleDateString()}
                        </td>
                        <td
                          className="px-3 py-2 text-[13px]"
                          style={{ color: C.sub }}
                        >
                          {t.project || "—"}
                        </td>
                        <td
                          className="px-3 py-2 text-[13px]"
                          style={{ color: C.sub }}
                        >
                          {t.woNumber || "—"}
                        </td>
                        <td
                          className="px-3 py-2 text-[13px] font-mono"
                          style={{ color: C.sub }}
                        >
                          {clock(t.start)}
                        </td>
                        <td
                          className="px-3 py-2 text-[13px] font-mono"
                          style={{ color: C.sub }}
                        >
                          {clock(t.end)}
                        </td>
                        <td
                          className="px-3 py-2 text-[13px] text-right font-mono"
                          style={{ color: C.text }}
                        >
                          {t.hours.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {sub.salary?.included && (
            <div className="mb-5">
              <Field label="Salary week">
                <span className="font-mono">{sub.salary.hours}h</span>
              </Field>
            </div>
          )}

          {v && (
            <div
              className="mb-5 rounded-md p-4"
              style={{
                backgroundColor: C.field,
                border: `1px solid ${C.line}`,
              }}
            >
              <p
                className="text-[12px] font-semibold mb-3"
                style={{ color: C.sub }}
              >
                {kindLabel(v.kind)} request · Rippling
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Field label="Type">{v.leaveTypeName}</Field>
                <Field label="Dates">
                  <span className="font-mono">
                    {fmtDate(v.startDate)} – {fmtDate(v.endDate)}
                  </span>
                </Field>
                <Field label="Requested">
                  <span className="font-mono">{fmtHours(v.totalMinutes)}</span>
                </Field>
                <Field label="Balance at submit">
                  <span className="font-mono">
                    {v.balanceUnlimited
                      ? "Unlimited"
                      : v.balanceUnverified
                        ? "Unverified"
                        : fmtHours(v.balanceMinutesAtSubmit)}
                  </span>
                </Field>
              </div>
              {v.weeks?.length > 0 && (
                <p className="text-[12px] mt-3" style={{ color: C.mute }}>
                  Payroll weeks:{" "}
                  {v.weeks
                    .map(
                      (w) => `${fmtDate(w.weekStart)} (${fmtHours(w.minutes)})`,
                    )
                    .join(" · ")}
                </p>
              )}
              {v.comments && (
                <p className="text-[12px] mt-2" style={{ color: C.sub }}>
                  “{v.comments}”
                </p>
              )}
              <p className="text-[12px] mt-2" style={{ color: C.mute }}>
                Rippling status:{" "}
                <span style={{ color: C.text }}>{v.ripplingStatus}</span>
                {v.ripplingError && (
                  <span style={{ color: C.bad }}> — {v.ripplingError}</span>
                )}
                {v.ripplingSyncedAt && (
                  <span style={{ color: C.mute }}>
                    {" "}
                    · checked{" "}
                    {format(new Date(v.ripplingSyncedAt), "MMM d, h:mm a")}
                  </span>
                )}
              </p>
              {isAdminView && sub.status === "pending" && (
                <div className="mt-3 flex items-center gap-3">
                  <button
                    onClick={checkLive}
                    className="text-[12px] font-semibold"
                    style={{ color: C.accent }}
                  >
                    Check live balance
                  </button>
                  {live && (
                    <span className="text-[12px]" style={{ color: C.text }}>
                      Now:{" "}
                      {live.unlimited
                        ? "Unlimited"
                        : fmtHours(live.availableMinutes)}
                    </span>
                  )}
                  {liveErr && (
                    <span className="text-[12px]" style={{ color: C.bad }}>
                      {liveErr}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {sub.expenses.length > 0 && (
            <div className="mb-5">
              <p
                className="text-[12px] font-semibold mb-2"
                style={{ color: C.sub }}
              >
                Material-order reimbursements
              </p>
              <div
                className="overflow-x-auto rounded-md"
                style={{ border: `1px solid ${C.line}` }}
              >
                <table className="min-w-full">
                  <thead>
                    <tr>
                      <Th>Invoice</Th>
                      <Th>MO</Th>
                      <Th>Project</Th>
                      <Th right>Total</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {sub.expenses.map((e, i) => (
                      <tr
                        key={e._id}
                        style={{
                          borderTop: i ? `1px solid ${C.lineSoft}` : "none",
                        }}
                      >
                        <td
                          className="px-3 py-2 text-[13px] font-mono"
                          style={{ color: C.text }}
                        >
                          #{e.invoiceNumber}
                        </td>
                        <td
                          className="px-3 py-2 text-[13px]"
                          style={{ color: C.sub }}
                        >
                          {e.moNumber != null ? `MO ${e.moNumber}` : "—"}
                        </td>
                        <td
                          className="px-3 py-2 text-[13px]"
                          style={{ color: C.sub }}
                        >
                          {e.project || "—"}{" "}
                          {e.unassigned && (
                            <Tag color={C.warn}>Was unassigned</Tag>
                          )}
                        </td>
                        <td
                          className="px-3 py-2 text-[13px] text-right font-mono"
                          style={{ color: C.text }}
                        >
                          {money(e.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {sub.notes && (
            <p className="text-[13px] mb-3" style={{ color: C.sub }}>
              <span style={{ color: C.mute }}>Notes: </span>
              {sub.notes}
            </p>
          )}
          {sub.status === "rejected" && sub.rejectionReason && (
            <p className="text-[13px] mb-3" style={{ color: C.bad }}>
              Rejected: {sub.rejectionReason}
            </p>
          )}
          {sub.reviewedBy?.displayName && sub.status !== "pending" && (
            <p className="text-[12px]" style={{ color: C.mute }}>
              Reviewed by {sub.reviewedBy.displayName}
              {sub.reviewedAt &&
                ` on ${format(new Date(sub.reviewedAt), "MMM d, yyyy")}`}
            </p>
          )}

          <div className="flex justify-end gap-2 mt-4 flex-wrap">
            {sub.status === "pending" && (isMine || isAdminView) && (
              <Btn disabled={busy} onClick={() => onCancel(sub)}>
                Cancel submission
              </Btn>
            )}
            {isAdminView && sub.status === "pending" && (
              <>
                <Btn
                  variant="danger"
                  disabled={busy}
                  onClick={() => onReject(sub)}
                >
                  Reject
                </Btn>
                <Btn
                  variant="success"
                  disabled={busy || declinedInRippling}
                  onClick={() => onApprove(sub)}
                >
                  {busy ? "Working…" : "Approve & add to payroll"}
                </Btn>
              </>
            )}
            {isAdminView && v?.ripplingStatus === "SYNC_FAILED" && (
              <Btn
                variant="primary"
                disabled={busy}
                onClick={() => onRetry(sub)}
              >
                Retry Rippling sync
              </Btn>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────
export default function PayrollSubmission() {
  const [meta, setMeta] = useState(null);
  const [me, setMe] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [tab, setTab] = useState("submit");
  const [alert, setAlert] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  // submit tab
  const [weekStart, setWeekStart] = useState("");
  const [eligible, setEligible] = useState(null);
  const [loadingEligible, setLoadingEligible] = useState(false);
  const [selTs, setSelTs] = useState(new Set());
  const [salaryOn, setSalaryOn] = useState(false);
  const [salaryHours, setSalaryHours] = useState(40);
  const [selBills, setSelBills] = useState(new Set());
  const [vacOn, setVacOn] = useState(false);
  const [vac, setVac] = useState({
    kind: "vacation",
    leaveTypeId: "",
    startDate: "",
    endDate: "",
    hoursPerDay: 8,
    comments: "",
  });
  const [balances, setBalances] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // collapsible sections + reimbursement search
  const [openSec, setOpenSec] = useState({
    hours: true,
    vac: false,
    exp: false,
    notes: false,
  });
  const toggleSec = (k) => setOpenSec((s) => ({ ...s, [k]: !s[k] }));
  const [expSearch, setExpSearch] = useState("");
  const [expSelectedOnly, setExpSelectedOnly] = useState(false);

  // lists
  const [mine, setMine] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [approvalFilter, setApprovalFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [overrideTarget, setOverrideTarget] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);

  const showError = (title, message) =>
    setAlert({
      open: true,
      type: "error",
      title,
      message,
      onConfirm: () => setAlert((p) => ({ ...p, open: false })),
    });
  const showOk = (title, message) =>
    setAlert({
      open: true,
      type: "success",
      title,
      message,
      onConfirm: () => setAlert((p) => ({ ...p, open: false })),
    });
  const errMsg = (err) => err.response?.data?.message || err.message;

  // ── loaders ─────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const m = await axios.get(`${API}/payrollSubmissions/meta`, {
          headers: headers(),
        });
        // Current user is only needed to flag "my" submissions; never block on it.
        axios
          .get(`${API}/auth/me`, { headers: headers() })
          .then((u) => setMe(u.data.user))
          .catch(() => {});
        setMeta(m.data);
        setVac((v) => ({ ...v, hoursPerDay: m.data.defaultHoursPerDay || 8 }));
        setSalaryHours(m.data.hoursPerWeek || 40);
        const current =
          m.data.periods.find((p) => p.isCurrent) || m.data.periods[0];
        if (current) setWeekStart(current.weekStart);
      } catch (err) {
        const status = err.response?.status;
        setLoadError(
          status === 404
            ? "The payrollSubmissions API wasn't found (404). Make sure the backend router is mounted at /api/payrollSubmissions and deployed."
            : `${errMsg(err)}${status ? ` (HTTP ${status})` : ""}`,
        );
      }
    })();
  }, []);

  const loadEligible = useCallback(async (week) => {
    if (!week) return;
    setLoadingEligible(true);
    try {
      const res = await axios.get(`${API}/payrollSubmissions/eligible`, {
        headers: headers(),
        params: { weekStart: week },
      });
      setEligible(res.data);
      setSelTs(new Set(res.data.timesheets.map((t) => t._id)));
      setSelBills(new Set());
      setExpSearch("");
      setExpSelectedOnly(false);
      setSalaryOn(!!res.data.salary?.available);
      if (res.data.salary?.hoursPerWeek)
        setSalaryHours(res.data.salary.hoursPerWeek);
    } catch (err) {
      setEligible(null);
      showError("Load Failed", errMsg(err));
    } finally {
      setLoadingEligible(false);
    }
  }, []);

  useEffect(() => {
    loadEligible(weekStart);
  }, [weekStart, loadEligible]);

  const loadBalances = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/payrollSubmissions/vacation/balances`,
        { headers: headers() },
      );
      setBalances(res.data);
    } catch (err) {
      setBalances({ linked: false, message: errMsg(err), balances: [] });
    }
  }, []);

  useEffect(() => {
    if (vacOn && !balances) loadBalances();
  }, [vacOn, balances, loadBalances]);

  const loadMine = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/payrollSubmissions`, {
        headers: headers(),
        params: { status: "all" },
      });
      setMine(res.data.submissions);
    } catch (err) {
      showError("Load Failed", errMsg(err));
    }
  }, []);

  const loadApprovals = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/payrollSubmissions`, {
        headers: headers(),
        params: { status: approvalFilter },
      });
      setApprovals(res.data.submissions);
    } catch (err) {
      showError("Load Failed", errMsg(err));
    }
  }, [approvalFilter]);

  // Pull the latest status of each Rippling leave request (approvals made in
  // Rippling don't reach us any other way yet). Server throttles to 30s.
  const syncFromRippling = useCallback(async (force = false) => {
    setSyncing(true);
    try {
      const res = await axios.post(
        `${API}/payrollSubmissions/sync`,
        { force },
        { headers: headers() },
      );
      if (!res.data.skipped) setLastSynced(new Date());
      return res.data;
    } catch {
      return null;
    } finally {
      setSyncing(false);
    }
  }, []);

  // On page load
  useEffect(() => {
    if (!meta?.canSubmit) return;
    (async () => {
      const r = await syncFromRippling();
      if (r?.changed?.length) {
        loadMine();
        if (meta.canApprove) loadApprovals();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta]);

  // Show the list immediately, then refresh it if the sync changed anything
  useEffect(() => {
    if (tab !== "mine") return;
    loadMine();
    (async () => {
      const r = await syncFromRippling();
      if (r?.changed?.length) loadMine();
    })();
  }, [tab, loadMine, syncFromRippling]);
  useEffect(() => {
    if (tab !== "approvals" || !meta?.canApprove) return;
    loadApprovals();
    (async () => {
      const r = await syncFromRippling();
      if (r?.changed?.length) loadApprovals();
    })();
  }, [tab, approvalFilter, meta, loadApprovals, syncFromRippling]);

  // ── derived values (hooks must stay above the early returns) ────────────
  const vacCalc = useMemo(() => {
    if (!vacOn || !vac.startDate || !vac.endDate) return null;
    const start = parseISO(vac.startDate);
    const end = parseISO(vac.endDate);
    if (
      Number.isNaN(start.getTime()) ||
      Number.isNaN(end.getTime()) ||
      end < start
    )
      return { invalid: true };
    const days = eachDayOfInterval({ start, end }).filter(
      (d) => !isWeekend(d),
    ).length;
    const minutes = Math.round(days * (Number(vac.hoursPerDay) || 0) * 60);
    const bal = balances?.balances?.find(
      (b) => b.leaveTypeId === vac.leaveTypeId,
    );
    const remaining =
      bal && !bal.unlimited && bal.availableMinutes != null
        ? bal.availableMinutes - minutes
        : null;
    return {
      days,
      minutes,
      bal,
      remaining,
      insufficient: remaining != null && remaining < 0,
    };
  }, [vacOn, vac, balances]);

  const tsHours = useMemo(
    () =>
      (eligible?.timesheets || [])
        .filter((t) => selTs.has(t._id))
        .reduce((s, t) => s + t.hours, 0),
    [eligible, selTs],
  );
  const billTotal = useMemo(
    () =>
      (eligible?.expenses || [])
        .filter((b) => selBills.has(b._id))
        .reduce((s, b) => s + b.total, 0),
    [eligible, selBills],
  );

  const visibleBills = useMemo(() => {
    const q = expSearch.trim().toLowerCase();
    return (eligible?.expenses || []).filter((b) => {
      if (expSelectedOnly && !selBills.has(b._id)) return false;
      if (!q) return true;
      return [
        b.invoiceNumber,
        b.moNumber != null ? `mo ${b.moNumber}` : "",
        b.moNumber,
        b.project,
        b.total != null ? b.total.toFixed(2) : "",
      ]
        .filter((x) => x != null && x !== "")
        .some((x) => String(x).toLowerCase().includes(q));
    });
  }, [eligible, expSearch, expSelectedOnly, selBills]);

  const toggleSet = (setter) => (id) =>
    setter((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const allVisibleSelected =
    visibleBills.length > 0 && visibleBills.every((b) => selBills.has(b._id));
  const toggleVisibleBills = () =>
    setSelBills((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleBills.forEach((b) => next.delete(b._id));
      else visibleBills.forEach((b) => next.add(b._id));
      return next;
    });

  const allTsSelected =
    (eligible?.timesheets?.length || 0) > 0 &&
    eligible.timesheets.every((t) => selTs.has(t._id));
  const toggleAllTs = () =>
    setSelTs(
      allTsSelected
        ? new Set()
        : new Set(eligible.timesheets.map((t) => t._id)),
    );

  const salaryIncluded = salaryOn && !!eligible?.salary?.available;
  const vacValid = vacOn && vacCalc && !vacCalc.invalid;
  const hasItems =
    selTs.size > 0 || selBills.size > 0 || salaryIncluded || vacValid;

  // Why the Submit button is disabled (shown to the user)
  let blockReason = "";
  if (eligible?.locked) blockReason = "This pay period is locked.";
  else if (vacOn && !vac.leaveTypeId)
    blockReason = "Choose a leave type for the time-off request.";
  else if (vacOn && (!vacCalc || vacCalc.invalid))
    blockReason = "Enter valid time-off dates.";
  else if (vacOn && vacCalc?.days === 0)
    blockReason = "Time-off dates are all weekend days.";
  else if (vacOn && vacCalc?.insufficient)
    blockReason = "Not enough leave balance.";
  else if (!hasItems) blockReason = "Select at least one item to submit.";
  const canSubmitNow = !blockReason && !submitting;

  // ── actions ─────────────────────────────────────────────────────────────
  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/payrollSubmissions`,
        {
          weekStart,
          timesheetIds: Array.from(selTs),
          salary: salaryIncluded
            ? { include: true, hours: Number(salaryHours) }
            : undefined,
          expenseBillIds: Array.from(selBills),
          vacation: vacOn
            ? { ...vac, hoursPerDay: Number(vac.hoursPerDay) }
            : undefined,
          notes,
        },
        { headers: headers() },
      );
      showOk(
        "Submitted",
        "Your payroll submission is waiting for admin approval.",
      );
      setVacOn(false);
      setBalances(null);
      setNotes("");
      setVac((v) => ({
        ...v,
        leaveTypeId: "",
        startDate: "",
        endDate: "",
        comments: "",
      }));
      loadEligible(weekStart);
      setTab("mine");
    } catch (err) {
      const m = errMsg(err);
      showError(
        /^Not enough/i.test(m) ? "Not enough leave balance" : "Submit Failed",
        m,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const refreshAfter = () => {
    loadApprovals();
    loadMine();
  };

  const approve = async (sub, overrideBalance = false) => {
    setBusyId(sub._id);
    try {
      const res = await axios.post(
        `${API}/payrollSubmissions/${sub._id}/approve`,
        { overrideBalance },
        { headers: headers() },
      );
      setOverrideTarget(null);
      showOk("Approved", res.data.message);
      refreshAfter();
    } catch (err) {
      if (err.response?.data?.needsOverride) setOverrideTarget(sub);
      else showError("Approve Failed", errMsg(err));
    } finally {
      setBusyId(null);
    }
  };

  const reject = async () => {
    if (!rejectReason.trim()) return;
    const sub = rejectTarget;
    setBusyId(sub._id);
    try {
      await axios.post(
        `${API}/payrollSubmissions/${sub._id}/reject`,
        { reason: rejectReason },
        { headers: headers() },
      );
      setRejectTarget(null);
      setRejectReason("");
      refreshAfter();
    } catch (err) {
      showError("Reject Failed", errMsg(err));
    } finally {
      setBusyId(null);
    }
  };

  const simplePost = async (sub, path, failTitle) => {
    setBusyId(sub._id);
    try {
      await axios.post(
        `${API}/payrollSubmissions/${sub._id}/${path}`,
        {},
        { headers: headers() },
      );
      refreshAfter();
      loadEligible(weekStart);
    } catch (err) {
      showError(failTitle, errMsg(err));
    } finally {
      setBusyId(null);
    }
  };

  // ── render ──────────────────────────────────────────────────────────────
  if (loadError)
    return (
      <div
        className="p-8 text-center text-sm rounded-lg mt-8"
        style={{ backgroundColor: C.bg }}
      >
        <p className="font-semibold mb-1" style={{ color: C.bad }}>
          Couldn't load payroll submission
        </p>
        <p style={{ color: C.sub }}>{loadError}</p>
      </div>
    );

  if (!meta)
    return (
      <div
        className="min-h-[240px] flex items-center justify-center text-sm"
        style={{ backgroundColor: C.bg, color: C.sub }}
      >
        Loading…
      </div>
    );

  if (!meta.canSubmit)
    return (
      <div
        className="p-8 text-center text-sm rounded-lg mt-8"
        style={{ backgroundColor: C.bg, color: C.sub }}
      >
        Payroll submissions are available to admins and project managers.
      </div>
    );

  const weekOptions = meta.periods.map((p) => ({
    value: p.weekStart,
    label: `${fmtDate(p.weekStart)} – ${fmtDate(p.weekEnd)}${p.isCurrent ? " (current)" : ""}`,
  }));
  const allBalances = balances?.balances || [];
  const kindBalances = allBalances.filter((b) =>
    vac.kind === "sick" ? SICK_RE.test(b.name) : !SICK_RE.test(b.name),
  );
  // If no type name matches (custom naming), fall back to everything.
  const leaveOptions = (kindBalances.length ? kindBalances : allBalances).map(
    (b) => ({
      value: b.leaveTypeId,
      label: `${b.name} — ${b.unlimited ? "Unlimited" : b.availableMinutes != null ? fmtHours(b.availableMinutes) + " available" : "balance unavailable"}`,
    }),
  );
  const pendingCount = approvals.filter((a) => a.status === "pending").length;
  const hasHourly = meta.payTypes.includes("hourly");
  const hasSalary = meta.payTypes.includes("salary");
  const totalHours =
    tsHours +
    (salaryIncluded ? Number(salaryHours) || 0 : 0) +
    (vacValid ? (vacCalc.minutes || 0) / 60 : 0);
  const expenses = eligible?.expenses || [];

  const manualSync = async () => {
    await syncFromRippling(true);
    loadMine();
    if (meta.canApprove) loadApprovals();
  };
  const syncBar = (
    <div className="flex items-center justify-end gap-3 mb-3">
      <span className="text-[12px]" style={{ color: C.mute }}>
        {lastSynced
          ? `Rippling checked ${format(lastSynced, "h:mm:ss a")}`
          : "Rippling not checked yet"}
      </span>
      <Btn disabled={syncing} onClick={manualSync}>
        {syncing ? "Syncing…" : "Sync with Rippling"}
      </Btn>
    </div>
  );

  const SummaryRow = ({ label, value, muted }) => (
    <div
      className="flex items-center justify-between py-2"
      style={{ borderTop: `1px solid ${C.lineSoft}` }}
    >
      <span className="text-[13px]" style={{ color: muted ? C.mute : C.sub }}>
        {label}
      </span>
      <span
        className="text-[13px] font-mono"
        style={{ color: muted ? C.mute : C.text }}
      >
        {value}
      </span>
    </div>
  );

  return (
    <div
      className="mx-auto px-4 py-6 rounded-lg mt-8 font-sans"
      style={{ backgroundColor: C.bg }}
    >
      <AlertDialog
        type={alert.type}
        title={alert.title}
        message={alert.message}
        confirmText="OK"
        cancelText="Close"
        open={alert.open}
        onClose={() => setAlert((p) => ({ ...p, open: false }))}
        onConfirm={alert.onConfirm}
      />

      {/* Reject modal */}
      {rejectTarget && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div
            className="p-6 rounded-lg shadow-2xl max-w-md w-full"
            style={{ backgroundColor: C.panel, border: `1px solid ${C.line}` }}
          >
            <h2
              className="text-[15px] font-semibold mb-2"
              style={{ color: C.text }}
            >
              Reject submission
            </h2>
            <p className="text-[13px] mb-3" style={{ color: C.sub }}>
              {rejectTarget.submittedBy?.displayName} will see this reason. Any
              pending Rippling time-off request is rejected too.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Reason"
              className={cls(inputCls, "h-auto py-2")}
            />
            <div className="flex justify-end gap-2 mt-4">
              <Btn
                onClick={() => {
                  setRejectTarget(null);
                  setRejectReason("");
                }}
              >
                Cancel
              </Btn>
              <Btn
                variant="danger"
                disabled={!rejectReason.trim() || busyId === rejectTarget._id}
                onClick={reject}
              >
                Reject
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* Balance override modal */}
      {overrideTarget && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div
            className="p-6 rounded-lg shadow-2xl max-w-md w-full"
            style={{ backgroundColor: C.panel, border: `1px solid ${C.line}` }}
          >
            <h2
              className="text-[15px] font-semibold mb-2"
              style={{ color: C.text }}
            >
              Balance not verified
            </h2>
            <p className="text-[13px] mb-5" style={{ color: C.sub }}>
              Rippling didn't return a readable balance for this vacation
              request when it was submitted. Check it in Rippling, then approve
              if it's available.
            </p>
            <div className="flex justify-end gap-2">
              <Btn onClick={() => setOverrideTarget(null)}>Cancel</Btn>
              <Btn
                variant="primary"
                onClick={() => approve(overrideTarget, true)}
              >
                Approve anyway
              </Btn>
            </div>
          </div>
        </div>
      )}

      <div className="mx-auto" style={{ maxWidth: 1400 }}>
        {/* Page header */}
        <div className="flex items-end justify-between gap-4 flex-wrap mb-5">
          <div>
            <p
              className="text-[11px] uppercase tracking-[0.14em] font-semibold"
              style={{ color: C.accent }}
            >
              Payroll
            </p>
            <h1
              className="text-[22px] font-semibold tracking-tight"
              style={{ color: C.text }}
            >
              Weekly Submission
            </h1>
          </div>
          {tab === "submit" && (
            <div style={{ minWidth: 300 }}>
              <Label>Pay period</Label>
              <Select
                options={weekOptions}
                value={weekOptions.find((o) => o.value === weekStart) || null}
                onChange={(s) => setWeekStart(s?.value || "")}
                styles={selectStyles}
                isSearchable={false}
              />
            </div>
          )}
        </div>

        {/* Tabs */}
        <div
          className="flex gap-6 mb-6"
          style={{ borderBottom: `1px solid ${C.line}` }}
        >
          {[
            { key: "submit", label: "Submit" },
            { key: "mine", label: "My submissions" },
            ...(meta.canApprove
              ? [
                  {
                    key: "approvals",
                    label: `Approvals${tab === "approvals" && pendingCount ? ` (${pendingCount})` : ""}`,
                  },
                ]
              : []),
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className="pb-3 -mb-px text-[13px] font-semibold transition-colors"
              style={{
                color: tab === t.key ? C.text : C.mute,
                borderBottom: `2px solid ${tab === t.key ? C.accent : "transparent"}`,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Submit ─────────────────────────────────────────────── */}
        {tab === "submit" && (
          <>
            {loadingEligible && (
              <p className="text-[13px] py-6" style={{ color: C.sub }}>
                Loading this week…
              </p>
            )}

            {!loadingEligible && eligible && (
              <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-6 items-start">
                {/* LEFT: sections */}
                <div>
                  {eligible.locked && (
                    <div
                      className="rounded-md px-4 py-3 mb-4 text-[13px]"
                      style={{
                        color: C.warn,
                        backgroundColor: `${C.warn}14`,
                        border: `1px solid ${C.warn}55`,
                      }}
                    >
                      This pay period is locked — nothing can be submitted for
                      it.
                    </div>
                  )}

                  {/* Hours */}
                  {(hasHourly || hasSalary) && (
                    <Section
                      title="My hours"
                      subtitle={
                        hasHourly
                          ? "Timesheets clocked in this pay week"
                          : "Salary hours for this pay week"
                      }
                      open={openSec.hours}
                      onToggle={() => toggleSec("hours")}
                      summary={
                        <span
                          className="text-[12px] font-mono"
                          style={{ color: C.sub }}
                        >
                          {(
                            tsHours +
                            (salaryIncluded ? Number(salaryHours) || 0 : 0)
                          ).toFixed(2)}
                          h
                        </span>
                      }
                    >
                      {hasHourly &&
                        (eligible.timesheets.length === 0 ? (
                          <p className="text-[13px]" style={{ color: C.mute }}>
                            No unsubmitted timesheets for this week.
                          </p>
                        ) : (
                          <div
                            className="overflow-x-auto rounded-md"
                            style={{ border: `1px solid ${C.line}` }}
                          >
                            <table className="min-w-full">
                              <thead>
                                <tr>
                                  <Th className="w-10">
                                    <Check
                                      checked={allTsSelected}
                                      onChange={toggleAllTs}
                                    />
                                  </Th>
                                  <Th>Date</Th>
                                  <Th>Project</Th>
                                  <Th>WO</Th>
                                  <Th>In</Th>
                                  <Th>Out</Th>
                                  <Th right>Break</Th>
                                  <Th right>Hours</Th>
                                </tr>
                              </thead>
                              <tbody>
                                {eligible.timesheets.map((t, i) => {
                                  const on = selTs.has(t._id);
                                  return (
                                    <tr
                                      key={t._id}
                                      onClick={() => toggleSet(setSelTs)(t._id)}
                                      className="cursor-pointer hover:brightness-125"
                                      style={{
                                        borderTop: i
                                          ? `1px solid ${C.lineSoft}`
                                          : "none",
                                        backgroundColor: on
                                          ? C.accentSoft
                                          : "transparent",
                                        boxShadow: on
                                          ? `inset 3px 0 0 ${C.accent}`
                                          : "none",
                                      }}
                                    >
                                      <td className="px-3 py-2.5">
                                        <Check
                                          checked={on}
                                          onChange={() =>
                                            toggleSet(setSelTs)(t._id)
                                          }
                                        />
                                      </td>
                                      <td
                                        className="px-3 py-2.5 text-[13px]"
                                        style={{ color: C.text }}
                                      >
                                        {new Date(t.start).toLocaleDateString()}
                                      </td>
                                      <td
                                        className="px-3 py-2.5 text-[13px]"
                                        style={{ color: C.sub }}
                                      >
                                        {t.project || "—"}
                                      </td>
                                      <td
                                        className="px-3 py-2.5 text-[13px]"
                                        style={{ color: C.sub }}
                                      >
                                        {t.woNumber || "—"}
                                      </td>
                                      <td
                                        className="px-3 py-2.5 text-[13px] font-mono"
                                        style={{ color: C.sub }}
                                      >
                                        {clock(t.start)}
                                      </td>
                                      <td
                                        className="px-3 py-2.5 text-[13px] font-mono"
                                        style={{ color: C.sub }}
                                      >
                                        {clock(t.end)}
                                      </td>
                                      <td
                                        className="px-3 py-2.5 text-[13px] text-right font-mono"
                                        style={{ color: C.sub }}
                                      >
                                        {t.breakMinutes
                                          ? `${t.breakMinutes}m`
                                          : "—"}
                                      </td>
                                      <td
                                        className="px-3 py-2.5 text-[13px] text-right font-mono"
                                        style={{ color: C.text }}
                                      >
                                        {t.hours.toFixed(2)}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                              <tfoot>
                                <tr
                                  style={{
                                    borderTop: `1px solid ${C.line}`,
                                    backgroundColor: C.panel2,
                                  }}
                                >
                                  <td
                                    colSpan={7}
                                    className="px-3 py-2.5 text-[12px] text-right font-semibold"
                                    style={{ color: C.sub }}
                                  >
                                    {selTs.size} of {eligible.timesheets.length}{" "}
                                    selected
                                  </td>
                                  <td
                                    className="px-3 py-2.5 text-[13px] text-right font-mono font-semibold"
                                    style={{ color: C.text }}
                                  >
                                    {tsHours.toFixed(2)}
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        ))}

                      {hasSalary && (
                        <div
                          className={cls(
                            "flex items-center gap-5 flex-wrap rounded-md p-4",
                            hasHourly && "mt-4",
                          )}
                          style={{
                            backgroundColor: C.field,
                            border: `1px solid ${C.line}`,
                          }}
                        >
                          {eligible.salary.available ? (
                            <>
                              <Switch
                                checked={salaryOn}
                                onChange={setSalaryOn}
                                label="Include salary week"
                              />
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  step="0.25"
                                  min="0"
                                  max="168"
                                  value={salaryHours}
                                  onChange={(e) =>
                                    setSalaryHours(e.target.value)
                                  }
                                  disabled={!salaryOn}
                                  className={cls(inputCls, "w-24 font-mono")}
                                />
                                <span
                                  className="text-[12px]"
                                  style={{ color: C.mute }}
                                >
                                  hours
                                </span>
                              </div>
                            </>
                          ) : (
                            <p
                              className="text-[13px]"
                              style={{ color: C.mute }}
                            >
                              {eligible.salary.alreadyAdded
                                ? "Salary for this week is already in payroll."
                                : "Salary for this week is already queued in a pending submission."}
                            </p>
                          )}
                        </div>
                      )}
                    </Section>
                  )}

                  {/* Vacation */}
                  <Section
                    title="Time off — vacation / sick"
                    subtitle="Requested in Rippling; your available balance is checked before you submit"
                    open={openSec.vac}
                    onToggle={() => toggleSec("vac")}
                    right={
                      <Switch
                        checked={vacOn}
                        onChange={(on) => {
                          setVacOn(on);
                          setOpenSec((s) => ({ ...s, vac: on }));
                        }}
                        label="Request time off"
                      />
                    }
                  >
                    {!vacOn && (
                      <p className="text-[13px]" style={{ color: C.mute }}>
                        Turn on “Request time off” to add vacation or a sick day
                        to this submission.
                      </p>
                    )}
                    {vacOn && !balances && (
                      <p className="text-[13px]" style={{ color: C.sub }}>
                        Loading your Rippling balances…
                      </p>
                    )}
                    {vacOn && balances && !balances.linked && (
                      <p className="text-[13px]" style={{ color: C.warn }}>
                        {balances.message ||
                          "Your account isn't linked to Rippling."}
                      </p>
                    )}
                    {vacOn && balances?.linked && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <Label>Type of time off</Label>
                          <div
                            className="inline-flex p-1 rounded-md"
                            style={{
                              backgroundColor: C.field,
                              border: `1px solid ${C.line}`,
                            }}
                          >
                            {["vacation", "sick"].map((k) => (
                              <button
                                key={k}
                                type="button"
                                onClick={() =>
                                  setVac((v) => ({
                                    ...v,
                                    kind: k,
                                    leaveTypeId: "",
                                  }))
                                }
                                className="px-4 py-1.5 text-[12px] font-semibold rounded transition-colors"
                                style={{
                                  backgroundColor:
                                    vac.kind === k
                                      ? C.accentSoft
                                      : "transparent",
                                  color: vac.kind === k ? C.accent : C.sub,
                                }}
                              >
                                {kindLabel(k)}
                              </button>
                            ))}
                          </div>
                          {vac.kind === "sick" && (
                            <p
                              className="text-[12px] mt-2"
                              style={{ color: C.mute }}
                            >
                              For a single sick day, use the same date for first
                              and last day.
                            </p>
                          )}
                        </div>
                        <div className="md:col-span-2">
                          <Label>Leave type</Label>
                          <Select
                            options={leaveOptions}
                            value={
                              leaveOptions.find(
                                (o) => o.value === vac.leaveTypeId,
                              ) || null
                            }
                            onChange={(s) =>
                              setVac((v) => ({
                                ...v,
                                leaveTypeId: s?.value || "",
                              }))
                            }
                            styles={selectStyles}
                            placeholder={
                              leaveOptions.length
                                ? "Select leave type…"
                                : "No leave balances found in Rippling"
                            }
                            isSearchable={false}
                          />
                        </div>
                        <div>
                          <Label>First day off</Label>
                          <input
                            type="date"
                            value={vac.startDate}
                            onChange={(e) =>
                              setVac((v) => ({
                                ...v,
                                startDate: e.target.value,
                                endDate:
                                  v.endDate && v.endDate >= e.target.value
                                    ? v.endDate
                                    : e.target.value,
                              }))
                            }
                            className={inputCls}
                            style={{ colorScheme: "dark" }}
                          />
                        </div>
                        <div>
                          <Label>Last day off</Label>
                          <input
                            type="date"
                            value={vac.endDate}
                            min={vac.startDate}
                            onChange={(e) =>
                              setVac((v) => ({ ...v, endDate: e.target.value }))
                            }
                            className={inputCls}
                            style={{ colorScheme: "dark" }}
                          />
                        </div>
                        <div>
                          <Label>Hours per day</Label>
                          <input
                            type="number"
                            step="0.25"
                            min="0"
                            max="24"
                            value={vac.hoursPerDay}
                            onChange={(e) =>
                              setVac((v) => ({
                                ...v,
                                hoursPerDay: e.target.value,
                              }))
                            }
                            className={inputCls}
                          />
                        </div>
                        <div>
                          <Label>Comments (optional)</Label>
                          <input
                            type="text"
                            value={vac.comments}
                            onChange={(e) =>
                              setVac((v) => ({
                                ...v,
                                comments: e.target.value,
                              }))
                            }
                            className={inputCls}
                          />
                        </div>

                        {vacCalc && (
                          <div
                            className="md:col-span-2 rounded-md p-4"
                            style={{
                              backgroundColor: C.field,
                              border: `1px solid ${vacCalc.insufficient ? C.bad : C.line}`,
                            }}
                          >
                            {vacCalc.invalid ? (
                              <p
                                className="text-[13px]"
                                style={{ color: C.bad }}
                              >
                                End date must be on or after the start date.
                              </p>
                            ) : (
                              <div className="grid grid-cols-3 gap-4">
                                <Field label="Working days">
                                  <span className="font-mono">
                                    {vacCalc.days}
                                  </span>
                                </Field>
                                <Field label="Requested">
                                  <span className="font-mono">
                                    {fmtHours(vacCalc.minutes)}
                                  </span>
                                </Field>
                                <Field label="Remaining after">
                                  <span
                                    className="font-mono"
                                    style={{
                                      color: vacCalc.insufficient
                                        ? C.bad
                                        : C.ok,
                                    }}
                                  >
                                    {vacCalc.bal?.unlimited
                                      ? "Unlimited"
                                      : vacCalc.remaining == null
                                        ? "—"
                                        : fmtHours(vacCalc.remaining)}
                                  </span>
                                </Field>
                                {vacCalc.insufficient && (
                                  <p
                                    className="col-span-3 text-[12px]"
                                    style={{ color: C.bad }}
                                  >
                                    Not enough balance for this request.
                                  </p>
                                )}
                                {vacCalc.days === 0 && (
                                  <p
                                    className="col-span-3 text-[12px]"
                                    style={{ color: C.warn }}
                                  >
                                    Those dates are all weekend days.
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </Section>

                  {/* Material-order reimbursements */}
                  <Section
                    title="Material-order reimbursements"
                    subtitle="Confirmed material-order bills you paid for"
                    open={openSec.exp}
                    onToggle={() => toggleSec("exp")}
                    summary={
                      expenses.length === 0 ? (
                        <span className="text-[12px]" style={{ color: C.mute }}>
                          None available
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <span
                            className="text-[12px]"
                            style={{ color: C.mute }}
                          >
                            {selBills.size} of {expenses.length} selected
                          </span>
                          <span
                            className="text-[12px] font-mono font-semibold px-2 py-0.5 rounded"
                            style={{
                              color: selBills.size ? C.accent : C.mute,
                              backgroundColor: selBills.size
                                ? C.accentSoft
                                : "transparent",
                              border: `1px solid ${selBills.size ? `${C.accent}55` : C.line}`,
                            }}
                          >
                            {money(billTotal)}
                          </span>
                        </span>
                      )
                    }
                  >
                    {expenses.length === 0 ? (
                      <p className="text-[13px]" style={{ color: C.mute }}>
                        No reimbursable material orders right now.
                      </p>
                    ) : (
                      <>
                        {/* toolbar */}
                        <div className="flex items-center gap-3 flex-wrap mb-3">
                          <div
                            className="relative flex-1"
                            style={{ minWidth: 240 }}
                          >
                            <span
                              className="absolute left-3 top-1/2 -translate-y-1/2"
                              style={{ color: C.mute }}
                            >
                              <SearchIcon />
                            </span>
                            <input
                              type="text"
                              value={expSearch}
                              onChange={(e) => setExpSearch(e.target.value)}
                              placeholder="Search invoice #, MO #, project or amount…"
                              className={cls(inputCls, "pl-9")}
                            />
                            {expSearch && (
                              <button
                                onClick={() => setExpSearch("")}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-[12px] px-1.5"
                                style={{ color: C.mute }}
                                aria-label="Clear search"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                          <Switch
                            checked={expSelectedOnly}
                            onChange={setExpSelectedOnly}
                            label="Selected only"
                          />
                          {selBills.size > 0 && (
                            <button
                              onClick={() => setSelBills(new Set())}
                              className="text-[12px] font-semibold"
                              style={{ color: C.accent }}
                            >
                              Clear selection
                            </button>
                          )}
                        </div>

                        <div
                          className="rounded-md overflow-hidden"
                          style={{ border: `1px solid ${C.line}` }}
                        >
                          <div
                            className="overflow-auto"
                            style={{ maxHeight: 420 }}
                          >
                            <table className="min-w-full">
                              <thead className="sticky top-0 z-10">
                                <tr>
                                  <Th className="w-10">
                                    <Check
                                      checked={allVisibleSelected}
                                      onChange={toggleVisibleBills}
                                      disabled={visibleBills.length === 0}
                                    />
                                  </Th>
                                  <Th>Invoice</Th>
                                  <Th>MO</Th>
                                  <Th>Project</Th>
                                  <Th>Status</Th>
                                  <Th right>Tax</Th>
                                  <Th right>Total</Th>
                                </tr>
                              </thead>
                              <tbody>
                                {visibleBills.length === 0 ? (
                                  <tr>
                                    <td
                                      colSpan={7}
                                      className="px-3 py-8 text-center text-[13px]"
                                      style={{ color: C.mute }}
                                    >
                                      No bills match
                                      {expSearch
                                        ? ` “${expSearch}”`
                                        : " this filter"}
                                      .
                                    </td>
                                  </tr>
                                ) : (
                                  visibleBills.map((b, i) => {
                                    const on = selBills.has(b._id);
                                    return (
                                      <tr
                                        key={b._id}
                                        onClick={() =>
                                          toggleSet(setSelBills)(b._id)
                                        }
                                        className="cursor-pointer hover:brightness-125"
                                        style={{
                                          borderTop: i
                                            ? `1px solid ${C.lineSoft}`
                                            : "none",
                                          backgroundColor: on
                                            ? C.accentSoft
                                            : "transparent",
                                          boxShadow: on
                                            ? `inset 3px 0 0 ${C.accent}`
                                            : "none",
                                        }}
                                      >
                                        <td className="px-3 py-2.5">
                                          <Check
                                            checked={on}
                                            onChange={() =>
                                              toggleSet(setSelBills)(b._id)
                                            }
                                          />
                                        </td>
                                        <td
                                          className="px-3 py-2.5 text-[13px] font-mono whitespace-nowrap"
                                          style={{ color: C.text }}
                                        >
                                          #{b.invoiceNumber}
                                        </td>
                                        <td
                                          className="px-3 py-2.5 text-[13px] whitespace-nowrap"
                                          style={{ color: C.sub }}
                                        >
                                          {b.moNumber != null
                                            ? `MO ${b.moNumber}`
                                            : "—"}
                                        </td>
                                        <td
                                          className="px-3 py-2.5 text-[13px]"
                                          style={{ color: C.sub }}
                                        >
                                          {b.project || "—"}
                                        </td>
                                        <td className="px-3 py-2.5">
                                          {b.unassigned ? (
                                            <span title="No one is assigned to this order — you'd be claiming it">
                                              <Tag color={C.warn}>
                                                Unassigned
                                              </Tag>
                                            </span>
                                          ) : (
                                            <Tag color={C.info}>Yours</Tag>
                                          )}
                                        </td>
                                        <td
                                          className="px-3 py-2.5 text-[13px] text-right font-mono"
                                          style={{ color: C.mute }}
                                        >
                                          {b.tax > 0 ? money(b.tax) : "—"}
                                        </td>
                                        <td
                                          className="px-3 py-2.5 text-[13px] text-right font-mono font-semibold"
                                          style={{ color: C.text }}
                                        >
                                          {money(b.total)}
                                        </td>
                                      </tr>
                                    );
                                  })
                                )}
                              </tbody>
                            </table>
                          </div>
                          <div
                            className="flex items-center justify-between px-4 py-2.5 text-[12px]"
                            style={{
                              backgroundColor: C.panel2,
                              borderTop: `1px solid ${C.line}`,
                              color: C.sub,
                            }}
                          >
                            <span>
                              Showing {visibleBills.length} of {expenses.length}{" "}
                              bills
                            </span>
                            <span>
                              Selected total:{" "}
                              <span
                                className="font-mono font-semibold"
                                style={{ color: C.text }}
                              >
                                {money(billTotal)}
                              </span>
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </Section>

                  {/* Notes */}
                  <Section
                    title="Notes for the approver"
                    subtitle="Optional"
                    open={openSec.notes}
                    onToggle={() => toggleSec("notes")}
                    summary={
                      notes.trim() ? <Tag color={C.info}>Added</Tag> : null
                    }
                  >
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      className={cls(inputCls, "h-auto py-2")}
                      placeholder="Anything the admin should know"
                    />
                  </Section>
                </div>

                {/* RIGHT: sticky summary */}
                <div className="xl:sticky xl:top-20">
                  <div
                    className="rounded-lg overflow-hidden"
                    style={{
                      backgroundColor: C.panel,
                      border: `1px solid ${C.line}`,
                    }}
                  >
                    <div
                      className="px-5 py-3.5"
                      style={{
                        backgroundColor: C.panel2,
                        borderBottom: `1px solid ${C.line}`,
                      }}
                    >
                      <h3
                        className="text-[14px] font-semibold"
                        style={{ color: C.text }}
                      >
                        Submission summary
                      </h3>
                      <p
                        className="text-[12px] mt-0.5"
                        style={{ color: C.mute }}
                      >
                        {(weekOptions.find((o) => o.value === weekStart) || {})
                          .label || "—"}
                      </p>
                    </div>
                    <div className="px-5 py-2">
                      {hasHourly && (
                        <SummaryRow
                          label={`Timesheets (${selTs.size})`}
                          value={`${tsHours.toFixed(2)}h`}
                          muted={selTs.size === 0}
                        />
                      )}
                      {hasSalary && (
                        <SummaryRow
                          label="Salary week"
                          value={
                            salaryIncluded
                              ? `${Number(salaryHours) || 0}h`
                              : "—"
                          }
                          muted={!salaryIncluded}
                        />
                      )}
                      <SummaryRow
                        label={kindLabel(vac.kind)}
                        value={vacValid ? `${fmtHours(vacCalc.minutes)}` : "—"}
                        muted={!vacValid}
                      />
                      <SummaryRow
                        label={`Reimbursements (${selBills.size})`}
                        value={money(billTotal)}
                        muted={selBills.size === 0}
                      />
                      <div
                        className="flex items-center justify-between py-3"
                        style={{ borderTop: `1px solid ${C.line}` }}
                      >
                        <span
                          className="text-[13px] font-semibold"
                          style={{ color: C.text }}
                        >
                          Total hours
                        </span>
                        <span
                          className="text-[15px] font-mono font-semibold"
                          style={{ color: C.text }}
                        >
                          {totalHours.toFixed(2)}h
                        </span>
                      </div>
                    </div>
                    <div className="px-5 pb-5">
                      <Btn
                        variant="primary"
                        full
                        disabled={!canSubmitNow}
                        onClick={submit}
                      >
                        {submitting ? "Submitting…" : "Submit for approval"}
                      </Btn>
                      {blockReason && (
                        <p
                          className="text-[12px] mt-2.5 text-center"
                          style={{ color: C.mute }}
                        >
                          {blockReason}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── My submissions ─────────────────────────────────────── */}
        {tab === "mine" && (
          <>
            {syncBar}
            {mine.length === 0 ? (
              <p
                className="text-[13px] py-10 text-center"
                style={{ color: C.mute }}
              >
                No submissions yet.
              </p>
            ) : (
              mine.map((s) => (
                <SubmissionCard
                  key={s._id}
                  sub={s}
                  isAdminView={false}
                  meId={me?.id || me?._id}
                  busy={busyId === s._id}
                  onCancel={(x) => simplePost(x, "cancel", "Cancel Failed")}
                />
              ))
            )}
          </>
        )}

        {/* ── Approvals (admin) ──────────────────────────────────── */}
        {tab === "approvals" && meta.canApprove && (
          <>
            {syncBar}
            <div
              className="flex gap-1 mb-4 p-1 rounded-md w-fit"
              style={{
                backgroundColor: C.panel,
                border: `1px solid ${C.line}`,
              }}
            >
              {["pending", "approved", "rejected", "all"].map((f) => (
                <button
                  key={f}
                  onClick={() => setApprovalFilter(f)}
                  className="px-3.5 py-1.5 text-[12px] font-semibold rounded capitalize transition-colors"
                  style={{
                    backgroundColor:
                      approvalFilter === f ? C.accentSoft : "transparent",
                    color: approvalFilter === f ? C.accent : C.sub,
                  }}
                >
                  {f}
                </button>
              ))}
            </div>
            {approvals.length === 0 ? (
              <p
                className="text-[13px] py-10 text-center"
                style={{ color: C.mute }}
              >
                Nothing here.
              </p>
            ) : (
              approvals.map((s) => (
                <SubmissionCard
                  key={s._id}
                  sub={s}
                  isAdminView
                  meId={me?.id || me?._id}
                  busy={busyId === s._id}
                  onApprove={(x) => approve(x)}
                  onReject={(x) => setRejectTarget(x)}
                  onCancel={(x) => simplePost(x, "cancel", "Cancel Failed")}
                  onRetry={(x) =>
                    simplePost(x, "retry-rippling", "Rippling Sync Failed")
                  }
                />
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
}
